import React from "react";
import { Alert, Card, Descriptions, Space, Tag } from "antd";
import { CrawlerRunRecord, EmptyProviderRecord } from "../../types/crawlerHealth";

interface LastRunCardProps {
  lastRun: CrawlerRunRecord | null;
  /**
   * Added 2026-09-18 (KI-39). Optional because the wire field is: `encodeDefaults = false` omits
   * `empty_providers` when the backend list is empty (see `HealthResponse.emptyProviders`), so the
   * healthy case arrives as `undefined`, not `[]`.
   */
  emptyProviders?: EmptyProviderRecord[];
  loading?: boolean;
}

/**
 * KI-39, 2026-09-18 — the reason this sits on the *last run* card rather than somewhere quieter.
 *
 * `CrawlerRunRecord.success` is run-level and still means `totalErrors === 0`, so a run in which
 * one provider silently committed nothing renders a green "Success" right above this line. That
 * gap is the whole finding: `bidmachine/IOS` reported clean with zero adapters on 201 of its 209
 * retained runs while nothing had been written for it since 2025-11-28. The warning is placed
 * immediately under the status it qualifies so the two are read together.
 *
 * It is not an error state — nothing failed — so it is a `warning`, never an `error`.
 */
const EmptyProvidersWarning: React.FC<{ providers: EmptyProviderRecord[] }> = ({ providers }) => {
  if (providers.length === 0) return null;
  const names = providers.map((p) => `${p.provider}/${p.platform.toLowerCase()}`).join(", ");
  return (
    <Alert
      type="warning"
      showIcon
      message={`${providers.length} provider${providers.length === 1 ? "" : "s"} returned zero adapters on the last run`}
      description={names}
    />
  );
};

function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return "N/A";
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${remainingSeconds}s`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "N/A";
  return new Date(iso).toLocaleString();
}

export const LastRunCard: React.FC<LastRunCardProps> = ({
  lastRun,
  emptyProviders,
  loading,
}) => {
  return (
    <Card title="Last Run" loading={loading}>
      {lastRun ? (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Status">
              {lastRun.success ? (
                <Tag color="green">Success</Tag>
              ) : (
                <Tag color="red">Failed</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Started">
              {formatDateTime(lastRun.startedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Duration">
              {formatDuration(lastRun.totalDurationMs)}
            </Descriptions.Item>
            <Descriptions.Item label="Triggered By">
              <Tag color={lastRun.triggeredBy === "manual" ? "blue" : "default"}>
                {lastRun.triggeredBy}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Adapters Found">
              {lastRun.totalAdaptersFound}
            </Descriptions.Item>
            <Descriptions.Item label="Errors">
              {lastRun.totalErrors > 0 ? (
                <Tag color="red">{lastRun.totalErrors}</Tag>
              ) : (
                <Tag color="green">0</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
          <EmptyProvidersWarning providers={emptyProviders ?? []} />
        </Space>
      ) : (
        <span>No runs recorded yet</span>
      )}
    </Card>
  );
};
